import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { Mic, Volume2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const VOICES = [
  { id: 'alloy', name: 'Alloy', description: 'Neutral and balanced' },
  { id: 'ash', name: 'Ash', description: 'Warm and friendly' },
  { id: 'ballad', name: 'Ballad', description: 'Smooth and melodic' },
  { id: 'coral', name: 'Coral', description: 'Bright and energetic' },
  { id: 'echo', name: 'Echo', description: 'Deep and resonant' },
  { id: 'sage', name: 'Sage', description: 'Calm and wise' },
  { id: 'shimmer', name: 'Shimmer', description: 'Light and airy' },
  { id: 'verse', name: 'Verse', description: 'Professional and clear' },
];

export default function VoiceSettings() {
  const { toast } = useToast();
  const { orgId } = useAuth();
  const [voice, setVoice] = useState('alloy');
  const [speed, setSpeed] = useState([1.0]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (orgId) loadVoiceSettings();
  }, [orgId]);

  const loadVoiceSettings = async () => {
    if (!orgId) return;

    const { data } = await supabase.functions.invoke('agent-memory-service', {
      body: {
        action: 'get_preferences',
        data: { org_id: orgId }
      }
    });

    if (data?.preferences?.voice_settings) {
      const settings = data.preferences.voice_settings.value;
      setVoice(settings.voice || 'alloy');
      setSpeed([settings.speed || 1.0]);
    }
  };

  const handleSave = async () => {
    if (!orgId) return;
    setSaving(true);

    try {
      const { error } = await supabase.functions.invoke('agent-memory-service', {
        body: {
          action: 'set_preference',
          data: {
            org_id: orgId,
            key: 'voice_settings',
            value: {
              voice,
              speed: speed[0]
            },
            confidence: 1.0
          }
        }
      });

      if (error) throw error;

      toast({
        title: 'Voice Settings Saved',
        description: 'Changes will apply to new voice sessions',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save voice settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mic className="w-5 h-5" />
          Voice Configuration
        </CardTitle>
        <CardDescription>
          Configure voice parameters for the OpenAI Realtime API sessions
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <label className="text-sm font-medium">Voice Selection</label>
          <Select value={voice} onValueChange={setVoice}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VOICES.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  <div className="flex flex-col">
                    <span className="font-medium">{v.name}</span>
                    <span className="text-xs text-muted-foreground">{v.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">Speech Speed</label>
            <span className="text-sm text-muted-foreground">{speed[0].toFixed(1)}x</span>
          </div>
          <Slider
            value={speed}
            onValueChange={setSpeed}
            min={0.5}
            max={2.0}
            step={0.1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Slower (0.5x)</span>
            <span>Normal (1.0x)</span>
            <span>Faster (2.0x)</span>
          </div>
        </div>

        <div className="pt-4 border-t">
          <Button onClick={handleSave} disabled={saving} className="w-full">
            {saving ? 'Saving...' : 'Save Voice Settings'}
          </Button>
        </div>

        <div className="p-4 bg-muted/50 rounded-lg">
          <p className="text-sm text-muted-foreground">
            <strong>Note:</strong> Voice settings are applied when creating new realtime sessions. 
            Active sessions will continue using their initial configuration.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
